import { startTransition, useState, useTransition } from "react";
import { i18n, setLocale } from "@/i18n";
import { useAuthStore } from "@/stores/authStore";
import type { ProfileUpdateInput, SupportedLocale } from "@/types/auth";

interface PersistLanguagePreferenceArgs {
  nextLanguage: SupportedLocale;
  previousLanguage: SupportedLocale;
  applyLocale: (locale: SupportedLocale) => Promise<void>;
  saveProfile: (input: ProfileUpdateInput) => Promise<unknown>;
}

export async function persistLanguagePreference({
  nextLanguage,
  previousLanguage,
  applyLocale,
  saveProfile,
}: PersistLanguagePreferenceArgs) {
  await applyLocale(nextLanguage);

  try {
    await saveProfile({
      language_pref: nextLanguage,
    });
  } catch (error) {
    await applyLocale(previousLanguage);
    throw error;
  }
}

export function useLanguagePreference() {
  const profile = useAuthStore((state) => state.profile);
  const saveProfile = useAuthStore((state) => state.saveProfile);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startSavingTransition] = useTransition();

  const language =
    profile?.language_pref ?? ((i18n.language as SupportedLocale | undefined) ?? "en");

  function changeLanguage(nextLanguage: SupportedLocale) {
    const previousLanguage = language;
    setError(null);

    startSavingTransition(() => {
      void persistLanguagePreference({
        nextLanguage,
        previousLanguage,
        applyLocale: setLocale,
        saveProfile: async (input) => saveProfile(input),
      }).catch((caughtError) => {
        startTransition(() => {
          setError(caughtError instanceof Error ? caughtError.message : "Save failed.");
        });
      });
    });
  }

  return {
    language,
    isSaving: isPending,
    error,
    canPersist: Boolean(profile),
    changeLanguage,
  };
}
